import type { APISharedLifestyleDay } from '@see-stella/types'

/**
 * The four LIFESTYLE (wearable) signals overlaid on the metric trend chart. Each is
 * a measurement only — no interpretation of the value (per ADR-0018 / design §14).
 * A signal overlays the skin trend as a second, right-axis-scaled line over the same
 * dates (see ScanPlayer's overlay picker + buildLifestyleOverlay).
 */
export type LifestyleSignalId = 'sleep' | 'hrv' | 'steps' | 'activity'

export interface LifestyleSignal {
  id: LifestyleSignalId
  /** Viewer-facing label (the app's own wording; "Stress recovery" for HRV per §14). */
  label: string
  /** Pull this signal's raw value from a day, or null when the provider had none. */
  valueOf(day: APISharedLifestyleDay): number | null
  /** Format the raw value per §14; a null (missing) value reads "No data". */
  format(value: number | null): string
}

/**
 * HRV descriptor (measurements-only, authored fresh per §14 — the app's weekly-report
 * HRV copy is value-interpretive/clinical, which the share safety gates ban). Factual,
 * no interpretation of the user's value, no clinical causation.
 */
export const LIFESTYLE_HRV_DESCRIPTION =
  'Heart rate variability: the day-to-day variation between your heartbeats, a ' +
  'common recovery signal. Shown as a daily average in milliseconds.'

const NO_DATA = 'No data'

/** Coerce the schema's `.nullish()` (number | null | undefined) to number | null. */
function num(v: number | null | undefined): number | null {
  return v ?? null
}

/** "7h 20m" — collapse whole hours ("8h") and sub-hour spans ("55m"). */
function formatSleep(value: number | null): string {
  if (value == null) return NO_DATA
  const total = Math.round(value)
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

export const LIFESTYLE_SIGNALS: readonly LifestyleSignal[] = [
  {
    id: 'sleep',
    label: 'Sleep',
    valueOf: (d) => num(d.sleepDurationMinutes),
    format: formatSleep,
  },
  {
    id: 'hrv',
    label: 'Stress recovery',
    valueOf: (d) => num(d.hrvAvgMs),
    format: (v) => (v == null ? NO_DATA : `${Math.round(v)} ms HRV`),
  },
  {
    id: 'steps',
    label: 'Steps',
    valueOf: (d) => num(d.steps),
    format: (v) =>
      v == null ? NO_DATA : Math.round(v).toLocaleString('en-US'),
  },
  {
    id: 'activity',
    label: 'Activity',
    valueOf: (d) => num(d.exerciseMinutes),
    format: (v) => (v == null ? NO_DATA : `${Math.round(v)} min`),
  },
] as const

const BY_ID: Record<LifestyleSignalId, LifestyleSignal> = Object.fromEntries(
  LIFESTYLE_SIGNALS.map((s) => [s.id, s])
) as Record<LifestyleSignalId, LifestyleSignal>

export function lifestyleSignal(id: LifestyleSignalId): LifestyleSignal {
  return BY_ID[id]
}
